import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useUserActions } from './hooks/useUserActions';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout';
// Login eager: necessario sem espera de chunk (1a tela do app)
import Login from './pages/Login';
// Demais paginas lazy — code splitting reduz first-load de ~1.24MB pra ~400KB.
// Cada rota baixa seu chunk so quando navegada. Vendor/MUI fica compartilhado.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Licenses = lazy(() => import('./pages/Licenses'));
const Users = lazy(() => import('./pages/Users'));
const Roles = lazy(() => import('./pages/Roles'));
const Holidays = lazy(() => import('./pages/Holidays'));
const ContactLinks = lazy(() => import('./pages/ContactLinks'));
const Downloads = lazy(() => import('./pages/Downloads'));
const OtimizeAgentDownloads = lazy(() => import('./pages/OtimizeAgentDownloads'));
const Settings = lazy(() => import('./pages/Settings'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerPortalUsers = lazy(() => import('./pages/CustomerPortalUsers'));
const ReplicadorClients = lazy(() => import('./pages/replicador/ReplicadorClients'));
const ReplicadorEndpoints = lazy(() => import('./pages/replicador/ReplicadorEndpoints'));
const ReplicadorSyncTables = lazy(() => import('./pages/replicador/ReplicadorSyncTables'));
const ReplicadorDashboard = lazy(() => import('./pages/replicador/ReplicadorDashboard'));
const ReplicadorBackups = lazy(() => import('./pages/replicador/ReplicadorBackups'));
const ReplicadorLogs = lazy(() => import('./pages/replicador/ReplicadorLogs'));
const ReplicadorClientConfig = lazy(() => import('./pages/replicador/ReplicadorClientConfig'));
const MonitorSistema = lazy(() => import('./pages/replicador/MonitorSistema'));
const SupportRequests = lazy(() => import('./pages/SupportRequests'));
const AtendimentoChat = lazy(() => import('./pages/atendimento/AtendimentoChat'));
const InovafarmaLabels = lazy(() => import('./pages/InovafarmaLabels'));
const InovafarmaLabelEditor = lazy(() => import('./pages/InovafarmaLabelEditor'));
const MonitorCertificados = lazy(() => import('./pages/replicador/MonitorCertificados'));
const OperationalAlerts = lazy(() => import('./pages/replicador/OperationalAlerts'));
const TefUpdates = lazy(() => import('./pages/TefUpdates'));
const AppVersions = lazy(() => import('./pages/AppVersions'));
const SyncConfig = lazy(() => import('./pages/SyncConfig'));
const Leads = lazy(() => import('./pages/Leads'));
const Atendimento = lazy(() => import('./pages/Atendimento'));
const PortalLogin = lazy(() => import('./pages/portal/PortalLogin'));
const PortalDashboard = lazy(() => import('./pages/portal/PortalDashboard'));
const PortalRegister = lazy(() => import('./pages/portal/PortalRegister'));
// Marketplace OtimizAPP HUB (hostname dedicado, sem Layout admin)
const HubMarketplace = lazy(() => import('./pages/HubMarketplace'));
const HubMarketplaceEmbed = lazy(() => import('./pages/HubMarketplaceEmbed'));
const HubMarketplaceReturn = lazy(() => import('./pages/HubMarketplaceReturn'));
const HubCheckout = lazy(() => import('./pages/HubCheckout'));
const HubCheckoutReturn = lazy(() => import('./pages/HubCheckoutReturn'));
const AppSignup = lazy(() => import('./pages/AppSignup'));
// Hub admin pages (admin.otimizapp.com.br)
const HubDashboard = lazy(() => import('./pages/HubDashboard'));
const HubPlansAdmin = lazy(() => import('./pages/HubPlansAdmin'));
const HubSubscriptionsAdmin = lazy(() => import('./pages/HubSubscriptionsAdmin'));
const HubPaymentsAdmin = lazy(() => import('./pages/HubPaymentsAdmin'));
const HubIntegrationAdmin = lazy(() => import('./pages/HubIntegrationAdmin'));
const MarketplaceUsersAdmin = lazy(() => import('./pages/MarketplaceUsersAdmin'));
const HubAdmin = lazy(() => import('./pages/HubAdmin'));

const PageSuspenseFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: 200 }}>
    <CircularProgress size={28} />
  </Box>
);

function PrivateRoute({ children, adminOnly = false, permission = null, requireAction = null }) {
  const { user, loading } = useAuth();
  const { hasAction, loading: actionsLoading } = useUserActions();

  if (loading) {
    return <div>Carregando...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Landing default: /dashboard (painel admin). Admin.otimizapp.com.br usa /hub/dashboard.
  const defaultLanding = (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.otimizapp.'))
    ? '/hub/dashboard' : '/dashboard';

  // adminOnly: apenas admin/superadmin (telas reais de gestão: Users, Settings)
  if (adminOnly && !['admin', 'superadmin'].includes(user.role)) {
    return <Navigate to={defaultLanding} />;
  }

  // permission: legacy (user_permission flags). superadmin sempre passa.
  if (permission && user.role !== 'superadmin' && !user.permissions?.[permission]) {
    return <Navigate to={defaultLanding} />;
  }

  // requireAction: novo RBAC granular. superadmin sempre passa (backend dá tudo).
  // Espera carregar actions pra evitar redirect prematuro.
  if (requireAction && user.role !== 'superadmin') {
    if (actionsLoading) return <div>Carregando permissões...</div>;
    if (!hasAction(requireAction)) return <Navigate to={defaultLanding} />;
  }

  return children;
}

function App() {
  // Domínios dedicados ao Hub (marketplace.otimizapp.com.br, hub.otimizapp.com.br) renderizam
  // SEM Layout admin — só rotas Hub. admin.otimizapp.com.br segue o fluxo admin (Hub-only sidebar
  // controlado em Layout.js).
  const isMarketplaceHost = typeof window !== 'undefined' &&
    (window.location.hostname.startsWith('marketplace.') ||
     window.location.hostname.startsWith('hub.'));

  if (isMarketplaceHost) {
    return (
      <Suspense fallback={<PageSuspenseFallback />}>
        <Routes>
          {/* Checkout por canal (fluxo nova-aba, sem HMAC) */}
          <Route path='/hub/checkout/return' element={<HubCheckoutReturn />} />
          <Route path='/hub/checkout/:channel' element={<HubCheckout />} />
          {/* Embed iframe Multiflow PRO (fluxo HMAC) */}
          <Route path='/hub/marketplace' element={<HubMarketplaceEmbed />} />
          <Route path='/hub/marketplace/return' element={<HubMarketplaceReturn />} />
          {/* Marketplace standalone — fallback */}
          <Route path='*' element={<HubMarketplace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageSuspenseFallback />}>
      <Routes>
      {/* Portal do Cliente (publico) */}
      <Route path="/portal" element={<PortalLogin />} />
      <Route path="/portal/register" element={<PortalRegister />} />
      <Route path="/portal/dashboard" element={<PortalDashboard />} />

      {/* Embed iframe Multiflow PRO */}

      {/* Admin Login */}
      <Route path="/downloads/otimize-agent" element={<OtimizeAgentDownloads />} />
      <Route path="/app-signup" element={<AppSignup />} />
      <Route path="/login" element={<Login />} />

      {/* Chat full-screen (top-nav proprio, sem sidebar). Rota canonica virou
          /atendimento (task 33: migrado 2026-07-04, removida pagina legada). */}
      <Route path="/atendimento" element={
        <PrivateRoute requireAction="atendimento.view">
          <AtendimentoChat />
        </PrivateRoute>
      } />
      {/* Retro-compat: quem tinha bookmark de /atendimento/chat cai no canonico */}
      <Route path="/atendimento/chat" element={<Navigate to="/atendimento" replace />} />

      {/* Admin Routes */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to={(typeof window !== 'undefined' && window.location.hostname.startsWith('admin.otimizapp.')) ? '/hub/dashboard' : '/dashboard'} replace />} />
        <Route path="dashboard" element={
          <PrivateRoute requireAction="dashboard.view">
            <Dashboard />
          </PrivateRoute>
        } />
        <Route path="licenses" element={
          <PrivateRoute requireAction="license.list">
            <Licenses />
          </PrivateRoute>
        } />
        <Route path="users" element={
          <PrivateRoute adminOnly requireAction="users.list">
            <Users />
          </PrivateRoute>
        } />
        <Route path="roles" element={
          <PrivateRoute adminOnly requireAction="users.permissions.update">
            <Roles />
          </PrivateRoute>
        } />
        <Route path="holidays" element={
          <PrivateRoute requireAction="settings.update">
            <Holidays />
          </PrivateRoute>
        } />
        <Route path="contact-links" element={
          <PrivateRoute requireAction="atendimento.contact_link.scan">
            <ContactLinks />
          </PrivateRoute>
        } />

        <Route path="support-requests" element={
          <PrivateRoute>
            <SupportRequests />
          </PrivateRoute>
        } />
        <Route path="downloads" element={
          <PrivateRoute requireAction="downloads.list">
            <Downloads />
          </PrivateRoute>
        } />
        <Route path="settings" element={
          <PrivateRoute adminOnly requireAction="settings.view">
            <Settings />
          </PrivateRoute>
        } />
        <Route path="customers" element={
          <PrivateRoute requireAction="customers.list">
            <Customers />
          </PrivateRoute>
        } />
        <Route path="hub/dashboard" element={
          <PrivateRoute adminOnly>
            <HubDashboard />
          </PrivateRoute>
        } />
        <Route path="hub/plans-admin" element={
          <PrivateRoute adminOnly>
            <HubPlansAdmin />
          </PrivateRoute>
        } />
        <Route path="hub/subscriptions" element={
          <PrivateRoute adminOnly>
            <HubSubscriptionsAdmin />
          </PrivateRoute>
        } />
        <Route path="hub/payments" element={
          <PrivateRoute adminOnly>
            <HubPaymentsAdmin />
          </PrivateRoute>
        } />
        <Route path="hub/integration" element={
          <PrivateRoute adminOnly>
            <HubIntegrationAdmin />
          </PrivateRoute>
        } />
        <Route path="marketplace/users" element={
          <PrivateRoute adminOnly>
            <MarketplaceUsersAdmin />
          </PrivateRoute>
        } />
        <Route path="hub-admin" element={
          <PrivateRoute adminOnly>
            <HubAdmin />
          </PrivateRoute>
        } />
        <Route path="etiquetas-inovafarma/edit/:id" element={
          <PrivateRoute requireAction="license.update">
            <InovafarmaLabelEditor />
          </PrivateRoute>
        } />
        <Route path="etiquetas-inovafarma" element={
          <PrivateRoute requireAction="license.update">
            <InovafarmaLabels />
          </PrivateRoute>
        } />
<Route path="portal-users" element={
          <PrivateRoute requireAction="portal_users.list">
            <CustomerPortalUsers />
          </PrivateRoute>
        } />        <Route path="sync-config" element={
          <PrivateRoute adminOnly requireAction="customers.sync.config">
            <SyncConfig />
          </PrivateRoute>
        } />
        {/* Monitor Routes (ex-Replicador) */}
        <Route path="monitor/dashboard" element={
          <PrivateRoute requireAction="monitor.dashboard.view">
            <ReplicadorDashboard />
          </PrivateRoute>
        } />
        <Route path="monitor/clients" element={
          <PrivateRoute requireAction="monitor.clients.view">
            <ReplicadorClients />
          </PrivateRoute>
        } />
        <Route path="monitor/backups" element={
          <PrivateRoute requireAction="monitor.backups.view">
            <ReplicadorBackups />
          </PrivateRoute>
        } />
        <Route path="monitor/endpoints" element={
          <PrivateRoute requireAction="monitor.endpoints.view">
            <ReplicadorEndpoints />
          </PrivateRoute>
        } />
        <Route path="monitor/sync-tables" element={
          <PrivateRoute requireAction="monitor.tables.view">
            <ReplicadorSyncTables />
          </PrivateRoute>
        } />
        <Route path="monitor/logs" element={
          <PrivateRoute requireAction="monitor.logs.view">
            <ReplicadorLogs />
          </PrivateRoute>
        } />
        <Route path="monitor/system" element={
          <PrivateRoute requireAction="monitor.system.view">
            <MonitorSistema />
          </PrivateRoute>
        } />
        <Route path="monitor/certificates" element={
          <PrivateRoute requireAction="monitor.certificates.view">
            <MonitorCertificados />
          </PrivateRoute>
        } />
        <Route path="monitor/alerts" element={
          <PrivateRoute requireAction="monitor.alerts.view">
            <OperationalAlerts />
          </PrivateRoute>
        } />
        <Route path="tef-updates" element={
          <PrivateRoute requireAction="tef_updates.view">
            <TefUpdates />
          </PrivateRoute>
        } />
        <Route path="app-versions" element={
          <PrivateRoute adminOnly>
            <AppVersions />
          </PrivateRoute>
        } />
        <Route path="monitor/clients/:clientId/config" element={
          <PrivateRoute requireAction="monitor.client.update">
            <ReplicadorClientConfig />
          </PrivateRoute>
        } />
        {/* Redirect old replicador routes to monitor */}
        <Route path="leads" element={
          <PrivateRoute adminOnly>
            <Leads />
          </PrivateRoute>
        } />
        <Route path="atendimento-plantoes" element={
          <PrivateRoute adminOnly>
            <Atendimento />
          </PrivateRoute>
        } />
        <Route path="replicador/*" element={<Navigate to="/monitor/dashboard" replace />} />
      </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
